import React from 'react';
import styled from '@emotion/styled';
import { Form, Button } from 'react-bootstrap';

import api from 'api';
import useUser from 'hooks/useUser';
import useFeedback from 'hooks/useFeedback';
import useGoBack from 'hooks/useGoBack';
import useCurrentApplication from 'hooks/useCurrentApplication';
import { formTitle } from 'helpers/forms';

import { GoBackButton } from 'components/elements/BackButton';
import SectionWrapper from 'components/layout/SectionWrapper';
import Modal from 'components/elements/BasicModal';
import Loader from 'components/elements/Loader';
import {
  getProgramTypeFromApplicationType,
  getSubTypeFromApplicationType,
} from 'utils/enums';

const FormSection = styled.div`
  margin-top: 1rem;

  @media only screen and (min-width: 1000px) {
    margin-left: 5%;
    margin-right: 5%;
  }
`;

const QuestionTitle = styled.div`
  font-size: 0.95rem;
  font-stretch: normal;
  font-style: normal;
  font-weight: 700;
  letter-spacing: normal;
  line-height: normal;
  color: #404040;
  background-color: ${(props) => (props.hasError ? '#f2c7c7' : 'var(--answer-background-color,#1ab39433)')};
  margin: 0;
  margin-bottom: 1rem;
  padding: 0.25em 0.44em;
`;

const ErrorMessage = styled.div`
  color: #d9534f;
  font-size: 0.9rem;
  margin-top: -0.5rem;
  margin-bottom: 0.5rem;
  padding-left: 2.5rem;
  font-weight: 500;
`;

const RadioGroup = styled.div`
  margin-bottom: 1.5rem;

  .form-check {
    margin-bottom: 0.5rem;
  }

  .form-check-input {
    width: 1.25rem;
    height: 1.25rem;
    margin-top: 0.15rem;
  }

  .form-check-label {
    color: #6c6c6c;
    font-size: 0.95rem;
    margin-left: 0.5rem;
  }
`;

const IndentedSection = styled.div`
  margin-left: 3.5rem;
  margin-top: 0.5rem;
`;

const CheckboxWrapper = styled.div`
  margin-bottom: 1rem;
  padding-left: 2.5rem;

  .form-check-input {
    width: 1.15rem;
    height: 1.15rem;
    margin-top: 0.2rem;
  }

  .form-check-label {
    color: #6c6c6c;
    font-size: 0.95rem;
    margin-left: 0.5rem;
  }
`;

const SubmitButtonWrapper = styled.div`
  margin-top: 2rem;
  display: flex;
  justify-content: flex-end;
  padding-bottom: 1rem;
`;

const CompleteButton = styled(Button)`
  background-color: #1ab394;
  border-color: #1ab394;
  border-radius: 1.214em;
  font-size: 0.875em;
  font-weight: 700;
  outline: none;
  padding: 0.5em 2.786em 0.6em;
  text-align: start;

  &:hover {
    background-color: #17a084;
    border-color: #17a084;
  }

  &:focus,
  &:active {
    background-color: #17a084 !important;
    border-color: #17a084 !important;
    box-shadow: none !important;
  }
`;

const ApplicationTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0 0 1.333em;
  color: #4a4a4a;
  padding-left: 0;
`;

const InitialApplication = () => {
  const { goBack } = useGoBack();
  const [verifyData, setVerifyData] = React.useState(null);
  const { setSuccess, setError } = useFeedback();
  const { appData, appGet } = useCurrentApplication();
  const { isAdmin } = useUser();

  const getPopulation = (populationValue) => {
    if (typeof populationValue === 'string') {
      const stringMap = {
        Adult: 'adult',
        Pediatric: 'pediatric',
        AdultAndPediatric: 'adult and pediatric',
      };
      return stringMap[populationValue] || populationValue.toLowerCase();
    }
    const map = {
      0: 'adult',
      1: 'pediatric',
      2: 'adult and pediatric',
    };
    return map[populationValue] ?? '';
  };

  const [isSample, setIsSample] = React.useState(appData?.isSample || false);

  const programType = getProgramTypeFromApplicationType(
    appData?.applicationType,
  );
  const subType = getSubTypeFromApplicationType(appData?.applicationType);

  const [formProgramType, setFormProgramType] = React.useState(
    programType === 'Fellowship' ? 'fellowship' : 'burn center',
  );
  const [fellowshipVerificationType, setFellowshipVerificationType] = React.useState(programType === 'Fellowship' ? subType || '' : '');
  const [burnCenterVerificationType, setBurnCenterVerificationType] = React.useState(programType === 'Burn Center' ? subType || '' : '');
  const [population, setPopulation] = React.useState(
    programType === 'Burn Center'
      ? getPopulation(appData?.applicationPopulation)
      : '',
  );
  const [formError, setFormError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  const validateForm = () => {
    if (!formProgramType) {
      setFormError('programType');
      return false;
    }

    if (formProgramType === 'fellowship' && !fellowshipVerificationType) {
      setFormError('fellowshipVerificationType');
      return false;
    }

    if (formProgramType === 'burn center') {
      if (!burnCenterVerificationType) {
        setFormError('burnCenterVerificationType');
        return false;
      }
      if (!population) {
        setFormError('population');
        return false;
      }
    }

    setFormError('');
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const applicationTypeMap = {
      fellowship: {
        verification: 'FellowshipVerification',
        're verification': 'FellowshipReverification',
      },
      'burn center': {
        'consultation only': 'BurnCenterConsultationOnly',
        verification: 'BurnCenterVerification',
        're verification': 'BurnCenterReverification',
        'focused review': 'BurnCenterFocusReview',
      },
    };

    const populationMap = {
      adult: 0,
      pediatric: 1,
      'adult and pediatric': 2,
    };

    const applicationType = formProgramType === 'fellowship'
      ? applicationTypeMap.fellowship[fellowshipVerificationType]
      : applicationTypeMap['burn center'][burnCenterVerificationType];

    const data = {
      isSample,
      applicationType,
      applicationPopulation:
        formProgramType === 'burn center' ? populationMap[population] : null,
    };

    setVerifyData(data);
  };

  const handleComplete = (data) => {
    setIsLoading(true);
    api
      .updateApplication({
        appId: appData.id,
        ...data,
      })
      .then(() => {
        setSuccess('Application updated successfully');
        appGet(); // Refresh the application data
        goBack();
      })
      .catch(({ response }) => {
        setError(response?.data?.detail || 'Error updating application');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (!appData) return <Loader />;

  return (
    <SectionWrapper
      title={`${appData.bcqpBurnCenterName} - ${formTitle.initialApplication}`}
    >
      <div>
        <GoBackButton onClick={() => goBack()} />
      </div>
      {verifyData && (
        <Modal
          title="Are you sure you would like to update this application?"
          body="Changes to verification type may require updating other related information."
          isOpen
          onClose={() => {
            setVerifyData(null);
          }}
          onConfirm={() => {
            handleComplete(verifyData);
            setVerifyData(null);
          }}
        />
      )}
      <Form onSubmit={handleSubmit}>
        <FormSection>
          <CheckboxWrapper>
            <Form.Check
              type="checkbox"
              label="This is a sample application for testing purposes."
              checked={isSample}
              onChange={(e) => setIsSample(e.target.checked)}
              disabled={!isAdmin}
            />
          </CheckboxWrapper>

          <ApplicationTitle>Verification Application</ApplicationTitle>

          <RadioGroup>
            <QuestionTitle hasError={formError === 'programType'}>
              What type of Program are you requesting? *
            </QuestionTitle>
            {formError === 'programType' && (
              <ErrorMessage>Response required.</ErrorMessage>
            )}
            <Form.Check
              type="radio"
              label="Fellowship Program"
              name="programType"
              value="fellowship"
              checked={formProgramType === 'fellowship'}
              onChange={(e) => setFormProgramType(e.target.value)}
              disabled={!isAdmin}
            />
            <Form.Check
              type="radio"
              label="Burn Center Program"
              name="programType"
              value="burn center"
              checked={formProgramType === 'burn center'}
              onChange={(e) => setFormProgramType(e.target.value)}
              disabled={!isAdmin}
            />
          </RadioGroup>

          {formProgramType === 'fellowship' && (
            <IndentedSection>
              <RadioGroup>
                <QuestionTitle
                  hasError={formError === 'fellowshipVerificationType'}
                >
                  What type of verification are you requesting in your
                  Fellowship Application *
                </QuestionTitle>
                {formError === 'fellowshipVerificationType' && (
                  <ErrorMessage>Response required.</ErrorMessage>
                )}
                <Form.Check
                  type="radio"
                  label="Verification"
                  name="fellowshipVerificationType"
                  value="verification"
                  checked={fellowshipVerificationType === 'verification'}
                  onChange={(e) => setFellowshipVerificationType(e.target.value)}
                  disabled={!isAdmin}
                />
                <Form.Check
                  type="radio"
                  label="Reverification"
                  name="fellowshipVerificationType"
                  value="re verification"
                  checked={fellowshipVerificationType === 're verification'}
                  onChange={(e) => setFellowshipVerificationType(e.target.value)}
                  disabled={!isAdmin}
                />
              </RadioGroup>
            </IndentedSection>
          )}

          {formProgramType === 'burn center' && (
            <IndentedSection>
              <RadioGroup>
                <QuestionTitle
                  hasError={formError === 'burnCenterVerificationType'}
                >
                  What type of verification are you requesting in your Burn
                  Center Application *
                </QuestionTitle>
                {formError === 'burnCenterVerificationType' && (
                  <ErrorMessage>Response required.</ErrorMessage>
                )}
                <Form.Check
                  type="radio"
                  label="Consultation Only"
                  name="burnCenterVerificationType"
                  value="consultation only"
                  checked={burnCenterVerificationType === 'consultation only'}
                  onChange={(e) => setBurnCenterVerificationType(e.target.value)}
                  disabled={!isAdmin}
                />
                <Form.Check
                  type="radio"
                  label="Verification"
                  name="burnCenterVerificationType"
                  value="verification"
                  checked={burnCenterVerificationType === 'verification'}
                  onChange={(e) => setBurnCenterVerificationType(e.target.value)}
                  disabled={!isAdmin}
                />
                <Form.Check
                  type="radio"
                  label="Reverification"
                  name="burnCenterVerificationType"
                  value="re verification"
                  checked={burnCenterVerificationType === 're verification'}
                  onChange={(e) => setBurnCenterVerificationType(e.target.value)}
                  disabled={!isAdmin}
                />
                <Form.Check
                  type="radio"
                  label="Focus Review"
                  name="burnCenterVerificationType"
                  value="focused review"
                  checked={burnCenterVerificationType === 'focused review'}
                  onChange={(e) => setBurnCenterVerificationType(e.target.value)}
                  disabled={!isAdmin}
                />
              </RadioGroup>

              <RadioGroup>
                <QuestionTitle hasError={formError === 'population'}>
                  For what population(s) are you seeking verification? *
                </QuestionTitle>
                {formError === 'population' && (
                  <ErrorMessage>Response required.</ErrorMessage>
                )}
                <Form.Check
                  type="radio"
                  label="Adult"
                  name="population"
                  value="adult"
                  checked={population === 'adult'}
                  onChange={(e) => setPopulation(e.target.value)}
                  disabled={!isAdmin}
                />
                <Form.Check
                  type="radio"
                  label="Pediatric"
                  name="population"
                  value="pediatric"
                  checked={population === 'pediatric'}
                  onChange={(e) => setPopulation(e.target.value)}
                  disabled={!isAdmin}
                />
                <Form.Check
                  type="radio"
                  label="Adult and Pediatric"
                  name="population"
                  value="adult and pediatric"
                  checked={population === 'adult and pediatric'}
                  onChange={(e) => setPopulation(e.target.value)}
                  disabled={!isAdmin}
                />
              </RadioGroup>
            </IndentedSection>
          )}

          {isAdmin && (
            <SubmitButtonWrapper>
              <CompleteButton type="submit" disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Update Application'}
              </CompleteButton>
            </SubmitButtonWrapper>
          )}
        </FormSection>
      </Form>
    </SectionWrapper>
  );
};

export default InitialApplication;
